export interface SizeEntry {
  tamanho: string;
  quantidade: number;
  modelo?: string;
}

export interface PersonalizationDetails {
  tecido?: string;
  cor?: string;
  tipo?: string;
  tamanhos: SizeEntry[];
  modelos?: Array<{ nome?: string; quantidade?: number }>;
  totalQuantidade?: number;
}

export interface ProductDetailBlock {
  finalItem: any;
  operationalItem: any | null;
  displayName: string;
  personalization: PersonalizationDetails | null;
}

function normalizeName(value?: string | null): string {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

// Um consumo é "rico" quando carrega os dados de variação (tamanhos/tecido).
// Discriminamos por presença desses campos — NÃO por `tipo`: numa camiseta DTF
// tanto o consumo do tecido quanto o da impressão vêm com tipo "impressao",
// então filtrar por tipo cairia no consumo errado. Mesmo critério do backend
// de produção (hasRich).
function hasRichPersonalization(entry: any): boolean {
  const meta = entry?.personalizacao_aplicada;
  if (!meta || typeof meta !== 'object') {
    return false;
  }

  const config = (meta as Record<string, unknown>).config as Record<string, unknown> | undefined;
  const tamanhos = (meta as Record<string, unknown>).tamanhos ?? config?.tamanhos;
  const hasTamanhos = Array.isArray(tamanhos) && tamanhos.length > 0;
  const tecido = (meta as Record<string, unknown>).tecido;
  const hasTecido = tecido != null && typeof tecido === 'object';

  return hasTamanhos || hasTecido;
}

function pickConsumption(item: any) {
  const consumptions = Array.isArray(item?.produto_personalizado_consumo)
    ? item.produto_personalizado_consumo
    : [];

  if (consumptions.length === 0) {
    return null;
  }

  // Personalizada (DTF) tem 2 consumos: tecido (rico, com tamanhos) + impressão
  // (fino). Preferimos o rico — é dele que saem tamanhos, cor e modelo no Detalhes.
  return (
    consumptions.find(hasRichPersonalization) ||
    consumptions.find((entry: any) => entry?.produto_personalizado?.pro_nome) ||
    consumptions[0]
  );
}

export function extractPersonalization(data: unknown): PersonalizationDetails | null {
  if (!data || typeof data !== 'object') {
    return null;
  }

  const record = data as Record<string, unknown>;
  const config = record.config as Record<string, unknown> | undefined;
  const tamanhosRaw = (record.tamanhos ?? config?.tamanhos) as Array<Record<string, unknown>> | undefined;

  const tamanhos: SizeEntry[] = [];
  if (Array.isArray(tamanhosRaw)) {
    for (const entry of tamanhosRaw) {
      const tamanho = entry?.tamanho;
      const quantidade = entry?.quantidade;
      if (tamanho != null && quantidade != null) {
        tamanhos.push({
          tamanho: String(tamanho),
          quantidade: Number(quantidade),
          modelo: typeof entry.modelo_camiseta === 'string' ? entry.modelo_camiseta : undefined,
        });
      }
    }
  }

  const tecidoRecord = record.tecido as Record<string, unknown> | undefined;
  const coresRecord = tecidoRecord?.cores as Record<string, unknown> | undefined;
  const metadata = record.metadata as Record<string, unknown> | undefined;
  const resumo = (record.resumo ?? config?.resumo) as Record<string, unknown> | undefined;

  const tecido = typeof tecidoRecord?.nome === 'string' ? tecidoRecord.nome : undefined;
  const cor = typeof coresRecord?.nome === 'string' ? coresRecord.nome : undefined;
  const tipo =
    (typeof record.tipo === 'string' ? record.tipo : undefined) ||
    (typeof record.tipo_config === 'string' ? record.tipo_config : undefined) ||
    (typeof config?.tipo_config === 'string' ? config.tipo_config : undefined);

  const modelos = Array.isArray(metadata?.modelos)
    ? (metadata.modelos as Array<{ nome?: string; quantidade?: number }>)
    : undefined;

  const totalFromSizes = tamanhos.reduce((sum, entry) => sum + entry.quantidade, 0);
  const totalQuantidade =
    (typeof metadata?.quantidadeTotal === 'number' ? metadata.quantidadeTotal : undefined) ||
    (typeof resumo?.quantidade_total === 'number' ? resumo.quantidade_total : undefined) ||
    (totalFromSizes > 0 ? totalFromSizes : undefined);

  if (!tecido && !cor && tamanhos.length === 0 && !tipo && !modelos?.length) {
    return null;
  }

  return { tecido, cor, tipo, tamanhos, modelos, totalQuantidade };
}

function getOperationalProductName(item: any): string | null {
  const fromContext = item?.linked_context?.operational_product?.pro_nome;
  if (typeof fromContext === 'string' && fromContext.trim()) {
    return fromContext;
  }

  const consumption = pickConsumption(item);
  const fromBase = consumption?.produto_base?.pro_nome;
  return typeof fromBase === 'string' && fromBase.trim() ? fromBase : null;
}

function findOperationalItem(items: any[], operationalName: string | null): any | null {
  if (!operationalName) {
    return null;
  }

  const normalized = normalizeName(operationalName);
  return (
    items.find(
      (entry) =>
        entry?.is_impressao_vinculada === true &&
        normalizeName(entry?.produto?.nome) === normalized
    ) ||
    items.find((entry) => normalizeName(entry?.produto?.nome) === normalized) ||
    null
  );
}

export function buildProductDetailBlocks(items: any[]): ProductDetailBlock[] {
  if (!Array.isArray(items) || items.length === 0) {
    return [];
  }

  const blocks: ProductDetailBlock[] = [];
  const pairedOperationalIds = new Set<number>();

  const commercialItems = items.filter((entry) => entry?.is_impressao_vinculada !== true);

  for (const finalItem of commercialItems) {
    const consumption = pickConsumption(finalItem);
    const operationalItem = findOperationalItem(items, getOperationalProductName(finalItem));

    if (operationalItem?.oit_id != null) {
      pairedOperationalIds.add(Number(operationalItem.oit_id));
    }

    const displayName =
      finalItem?.linked_context?.final_product?.pro_nome ||
      consumption?.produto_personalizado?.pro_nome ||
      finalItem?.produto?.nome ||
      'Produto não informado';

    blocks.push({
      finalItem,
      operationalItem,
      displayName,
      personalization: extractPersonalization(consumption?.personalizacao_aplicada),
    });
  }

  for (const entry of items) {
    if (entry?.is_impressao_vinculada !== true) {
      continue;
    }

    const oitId = Number(entry?.oit_id);
    if (pairedOperationalIds.has(oitId)) {
      continue;
    }

    blocks.push({
      finalItem: entry,
      operationalItem: null,
      displayName: entry?.produto?.nome || 'Produto não informado',
      personalization: extractPersonalization(pickConsumption(entry)?.personalizacao_aplicada),
    });
  }

  return blocks;
}

export interface SizeModelGroup {
  modelo: string;
  tamanhos: SizeEntry[];
}

export function groupSizesByModelo(
  tamanhos: SizeEntry[],
  modelos?: Array<{ nome?: string; quantidade?: number }>
): SizeModelGroup[] {
  if (tamanhos.length === 0) {
    return [];
  }

  const withModelo = tamanhos.filter((entry) => entry.modelo);
  const withoutModelo = tamanhos.filter((entry) => !entry.modelo);

  if (withModelo.length === 0) {
    return [{ modelo: '', tamanhos }];
  }

  const groupsMap = new Map<string, SizeEntry[]>();
  for (const entry of withModelo) {
    const key = entry.modelo as string;
    const current = groupsMap.get(key) ?? [];
    current.push(entry);
    groupsMap.set(key, current);
  }

  const orderedNames: string[] = [];
  if (Array.isArray(modelos)) {
    for (const modelo of modelos) {
      if (modelo?.nome && groupsMap.has(modelo.nome) && !orderedNames.includes(modelo.nome)) {
        orderedNames.push(modelo.nome);
      }
    }
  }

  for (const name of groupsMap.keys()) {
    if (!orderedNames.includes(name)) {
      orderedNames.push(name);
    }
  }

  const groups = orderedNames.map((modelo) => ({
    modelo,
    tamanhos: groupsMap.get(modelo) ?? [],
  }));

  if (withoutModelo.length > 0) {
    groups.push({ modelo: 'Outros', tamanhos: withoutModelo });
  }

  return groups;
}

export function formatItemMeasure(item: any): string {
  const quantity = item?.quantidade;
  if (quantity == null) {
    return '—';
  }

  const unitDescription = String(item?.produto?.unidade?.descricao || '').toUpperCase();
  const unitSigla = String(item?.produto?.unidade?.sigla || '').toLowerCase();

  if (unitDescription.includes('METRO') || unitSigla === 'm') {
    return `${quantity} m`;
  }

  return `${quantity} ${unitSigla || 'un'}`;
}

export function formatCurrency(value: unknown): string | null {
  if (value == null || value === '') {
    return null;
  }

  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return null;
  }

  return parsed.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}
